import { useGetCountryName } from 'hooks/ppoints/useGetCountryName';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Countries with no Shopify market yet, styled like `ShippingZoneRateStep`'s `NO_ZONE_WARNING`
 * banner (warning banner, body copy, a list of the affected countries) instead of the settings
 * page's plain `PickupPointSettingsWithoutMarket` section — same "add a market" action underneath,
 * since (unlike a shipping zone) a market isn't created per country.
 */
const MarketRateWithoutMarketBanner: React.FC<MarketRateWithoutMarketBannerProps> = ({
    countryCodes,
    onMarketCreated,
}) => {
    const { t } = useTranslation();
    const { getTranslatedCountryName } = useGetCountryName();
    const [isCreating, setIsCreating] = useState(false);

    const openCreateMarket = async () => {
        setIsCreating(true);

        try {
            const activity = await shopify.intents.invoke?.('create:shopify/Market');
            const response = await activity?.complete;

            if (response?.code === 'ok') onMarketCreated?.();
        } finally {
            setIsCreating(false);
        }
    };

    return (
        <div className="pt-2">
            <s-banner tone="warning" heading={t('WIZARD.SHIPPING_ZONE_RATE_STEP.NO_ZONE_WARNING.HEADING')}>
                <s-text>{t('SHIPPING_PROVIDERS.PICKUP_POINT_SETTINGS.WITHOUT_MARKET.DESCRIPTION')}</s-text>
                <div className="grid gap-2 pt-2">
                    {countryCodes.map((countryCode) => (
                        <s-text key={countryCode}>{getTranslatedCountryName(countryCode)}</s-text>
                    ))}
                </div>
                <div className="flex flex-row pt-2">
                    <s-button icon="plus" loading={isCreating || undefined} onClick={openCreateMarket}>
                        {t('SHIPPING_PROVIDERS.PICKUP_POINT_SETTINGS.WITHOUT_MARKET.ADD_MARKET')}
                    </s-button>
                </div>
            </s-banner>
        </div>
    );
};

interface MarketRateWithoutMarketBannerProps {
    countryCodes: string[];
    onMarketCreated?: () => void;
}

export default MarketRateWithoutMarketBanner;
